import { useEffect, useMemo, useState } from 'react';
import { ArrowUp, File, Folder } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { cn } from '../../../lib/utils';
import { mssqlListServerFiles } from '../services/backupRestoreApi';
import type { MssqlServerFileEntry } from '../types/backupRestore';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import {
  getServerParentDirectory,
  normalizeServerDirectoryPath,
} from '../utils/serverPathUtils';

type PickerMode = 'directory' | 'file';

type ServerPathPickerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  mode: PickerMode;
  initialDirectoryPath: string;
  title: string;
  description: string;
  fileExtensionFilter?: string;
  onSelectPath: (path: string) => void;
};

function isMatchingExtension(entry: MssqlServerFileEntry, extension: string): boolean {
  if (entry.type !== 'file') return true;
  return entry.name.toLowerCase().endsWith(extension.toLowerCase());
}

export function ServerPathPickerDialog({
  open,
  onOpenChange,
  connectionId,
  mode,
  initialDirectoryPath,
  title,
  description,
  fileExtensionFilter,
  onSelectPath,
}: ServerPathPickerDialogProps) {
  const [pathDraft, setPathDraft] = useState('');
  const [directoryPath, setDirectoryPath] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<MssqlServerFileEntry | null>(null);

  useEffect(() => {
    if (!open) return;

    const normalized = normalizeServerDirectoryPath(initialDirectoryPath);
    setPathDraft(normalized);
    setDirectoryPath(normalized);
    setSelectedEntry(null);
  }, [open, initialDirectoryPath]);

  const listQuery = useQuery({
    queryKey: ['backup-restore', 'mssql', 'server-files', connectionId, directoryPath],
    queryFn: async () => {
      const result = await mssqlListServerFiles({ connectionId, directoryPath });
      if (!result.success) {
        throw new Error(result.error || 'Unable to browse server files. Type the path manually.');
      }
      return result.entries ?? [];
    },
    enabled: open && Boolean(connectionId) && Boolean(directoryPath.trim()),
    retry: false,
  });

  const entries = useMemo(() => {
    const raw = listQuery.data ?? [];
    const filtered = fileExtensionFilter
      ? raw.filter((entry) => isMatchingExtension(entry, fileExtensionFilter))
      : raw;

    return [...filtered].sort((a, b) => {
      if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [listQuery.data, fileExtensionFilter]);

  const handleGo = () => {
    const normalized = normalizeServerDirectoryPath(pathDraft);
    setDirectoryPath(normalized);
    setPathDraft(normalized);
    setSelectedEntry(null);
  };

  const handleUp = () => {
    const parent = getServerParentDirectory(directoryPath);
    if (!parent) return;
    setDirectoryPath(parent);
    setPathDraft(parent);
    setSelectedEntry(null);
  };

  const canSelectCurrentDirectory = mode === 'directory' && Boolean(directoryPath.trim());
  const canSelectEntry =
    mode === 'file'
      ? selectedEntry?.type === 'file'
      : selectedEntry?.type === 'directory';

  const confirmLabel = mode === 'directory' ? 'Select folder' : 'Select file';

  const handleConfirm = () => {
    if (mode === 'directory') {
      const selectedPath =
        selectedEntry?.type === 'directory' ? selectedEntry.fullPath : directoryPath;
      onSelectPath(selectedPath);
      onOpenChange(false);
      return;
    }

    if (selectedEntry?.type === 'file') {
      onSelectPath(selectedEntry.fullPath);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="server-path-picker-dialog" className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="mt-1">{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleUp}
              disabled={!directoryPath.trim() || directoryPath === '/' || /^[a-zA-Z]:\\$/.test(directoryPath)}
              data-testid="server-path-picker-up"
              title="Up one folder"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Input
              value={pathDraft}
              onChange={(e) => setPathDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleGo();
                }
              }}
              placeholder="Server path (e.g. C:\\Backups or /var/opt/mssql/backups)"
              data-testid="server-path-picker-path-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleGo}
              data-testid="server-path-picker-go"
            >
              Go
            </Button>
          </div>

          {listQuery.isFetching ? (
            <div className="flex items-center justify-center py-8 gap-3 text-muted-foreground">
              <RandomDotmSpinner size={20} />
              <span className="text-sm">Loading...</span>
            </div>
          ) : listQuery.isError ? (
            <div className="rounded-md border border-border bg-muted/20 p-3 text-sm text-muted-foreground">
              {(listQuery.error as Error).message}
            </div>
          ) : (
            <ScrollArea className="max-h-[320px] rounded-md border border-border">
              <div className="divide-y divide-border">
                {entries.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground">No items found.</div>
                ) : (
                  entries.map((entry, entryIndex) => {
                    const isSelected = selectedEntry?.fullPath === entry.fullPath;
                    const isDisabled =
                      mode === 'directory' ? entry.type !== 'directory' : false;

                    return (
                      <button
                        key={`${entry.type}:${entry.fullPath}`}
                        type="button"
                        className={cn(
                          'w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-muted/50',
                          isSelected && 'bg-muted/60',
                          isDisabled && 'opacity-60 cursor-not-allowed'
                        )}
                        onClick={() => {
                          if (isDisabled) return;
                          setSelectedEntry(entry);
                        }}
                        onDoubleClick={() => {
                          if (entry.type === 'directory') {
                            const next = normalizeServerDirectoryPath(entry.fullPath);
                            setDirectoryPath(next);
                            setPathDraft(next);
                            setSelectedEntry(null);
                            return;
                          }

                          if (mode === 'file' && entry.type === 'file') {
                            onSelectPath(entry.fullPath);
                            onOpenChange(false);
                          }
                        }}
                        data-testid={`server-path-picker-entry-${entry.type}-${entryIndex}`}
                      >
                        {entry.type === 'directory' ? (
                          <Folder className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <File className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="truncate">{entry.name}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          )}

          <p className="text-xs text-muted-foreground">
            Server browsing is best-effort and may require additional SQL Server permissions. If it’s unavailable, type the path manually.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={mode === 'directory' ? !(canSelectCurrentDirectory || canSelectEntry) : !canSelectEntry}
            data-testid="server-path-picker-confirm"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
